import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useCart } from '../context/CartContext';
import { useToast } from '../components/Toast';

const PAGE_LIMIT = 24;

function CardSkeleton() {
  return (
    <div className="card card-skel">
      <div className="skel skel-img" />
      <div className="card-body">
        <div className="skel skel-line short" />
        <div className="skel skel-line long" />
        <div className="skel skel-line" />
        <div className="skel skel-btn" />
      </div>
    </div>
  );
}

function CatalogPage() {
  const [models, setModels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const { addItem, items: cartItems } = useCart();
  const toast = useToast();
  const cartQty = (id) => cartItems.find(x => x.model_id === id)?.quantity || 0;
  const handleAddToCart = (e, model) => {
    e.preventDefault(); e.stopPropagation();
    addItem(model.id, 1);
    toast.success(`${model.title} — в корзине`);
  };

  useEffect(() => { api.get('/categories').then(r => setCategories(r.data)).catch(()=>{}); }, []);
  useEffect(() => { setPage(1); }, [activeCategory, search, sort]);
  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (activeCategory) p.set('category', activeCategory);
    if (search) p.set('search', search);
    if (sort) p.set('sort', sort);
    p.set('page', page);
    p.set('limit', PAGE_LIMIT);
    api.get(`/models?${p}`).then(r => {
      const data = r.data || {};
      const items = Array.isArray(data) ? data : (data.items || []);
      setModels(items);
      setPages(data.pages || 1);
      setTotal(data.total != null ? data.total : items.length);
    }).catch(()=>{}).finally(() => setLoading(false));
  }, [activeCategory, search, sort, page]);

  const goTo = (n) => {
    const next = Math.min(pages, Math.max(1, n));
    if (next === page) return;
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = () => {
    const out = [];
    const add = (v) => out.push(v);
    if (pages <= 7) { for (let i = 1; i <= pages; i++) add(i); return out; }
    add(1);
    if (page > 3) add('…');
    const start = Math.max(2, page - 1);
    const end = Math.min(pages - 1, page + 1);
    for (let i = start; i <= end; i++) add(i);
    if (page < pages - 2) add('…');
    add(pages);
    return out;
  };

  return (
    <main>
      <section className="hero"><h1>3D-печатные модели</h1><p>Качественные модели, напечатанные на FDM/SLA принтерах</p></section>
      <div className="container">
        <div className="filters">
          <div className="filters-top">
            <input type="text" className="search-input" placeholder="Поиск моделей..." value={search} onChange={e => setSearch(e.target.value)} />
            <select className="sort-select select-pretty" value={sort} onChange={e=>setSort(e.target.value)}>
              <option value="newest">Сначала новые</option><option value="oldest">Сначала старые</option>
              <option value="price_asc">Цена: по возрастанию</option><option value="price_desc">Цена: по убыванию</option>
            </select>
          </div>
          <div className="filters-categories hide-mobile">
            <button className={`filter-btn ${activeCategory===''?'active':''}`} onClick={()=>setActiveCategory('')}>Все</button>
            {categories.map(cat => (<button key={cat.id} className={`filter-btn ${activeCategory===cat.slug?'active':''}`} onClick={()=>setActiveCategory(cat.slug)}>{cat.name}</button>))}
          </div>
          <select className="select-pretty filters-category-select show-mobile-block" value={activeCategory} onChange={e => setActiveCategory(e.target.value)} aria-label="Категория">
            <option value="">Все категории</option>
            {categories.map(cat => <option key={cat.id} value={cat.slug}>{cat.name}</option>)}
          </select>
        </div>
        {loading ? (
          <div className="cards-grid">{Array.from({length: 8}).map((_, i) => <CardSkeleton key={i} />)}</div>
        ) : models.length===0 ? (
          <div className="empty-state">
            <div className="icon">&#128270;</div>
            <p>{search || activeCategory ? 'По вашему запросу ничего не нашлось' : 'Моделей пока нет — скоро добавим'}</p>
            {(search || activeCategory) && (
              <div className="empty-actions">
                <button className="btn btn-outline btn-sm" onClick={() => { setSearch(''); setActiveCategory(''); }}>Сбросить фильтры</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="cards-grid">{models.map(m => {
              const inCart = cartQty(m.id);
              return (
              <Link to={`/model/${m.id}`} key={m.id} className="card">
                {m.thumbnail_url ? <img src={m.thumbnail_url} alt={m.title} className="card-image" loading="lazy"/> : <div className="card-image-placeholder">&#9883;</div>}
                <div className="card-body">
                  {m.category_name && <div className="card-category">{m.category_name}</div>}
                  <h3 className="card-title">{m.title}</h3>
                  {m.description && <p className="card-description">{m.description}</p>}
                  <div className="card-spacer" />
                  <div className="card-price-row">
                    <div className="card-price">{Number(m.final_price || m.display_price || m.price).toLocaleString('ru-RU')} &#8376;</div>
                    {m.quantity > 1 && <span className="card-qty">{m.quantity} шт</span>}
                  </div>
                  <button className={`btn btn-primary btn-sm btn-full card-cart-btn ${inCart ? 'is-in-cart' : ''}`} onClick={e => handleAddToCart(e, m)}>
                    {inCart ? <>В корзине<span className="btn-cart-count">{inCart}</span></> : 'В корзину'}
                  </button>
                </div>
              </Link>
            );})}</div>
            {pages > 1 && (
              <nav className="pagination" aria-label="Страницы каталога">
                <button className="page-btn" onClick={() => goTo(page - 1)} disabled={page <= 1} aria-label="Предыдущая">&#8249;</button>
                {pageNumbers().map((n, i) => n === '…'
                  ? <span key={`e${i}`} className="page-ellipsis">…</span>
                  : <button key={n} className={`page-btn ${n===page?'active':''}`} onClick={() => goTo(n)}>{n}</button>
                )}
                <button className="page-btn" onClick={() => goTo(page + 1)} disabled={page >= pages} aria-label="Следующая">&#8250;</button>
                <span className="page-info">{total} моделей</span>
              </nav>
            )}
          </>
        )}
      </div>
    </main>
  );
}
export default CatalogPage;
